import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useAgent } from "@/lib/agentContext";
import api from "@/lib/api";
import { TrendingUp, ArrowRight, MapPin, ShieldCheck, ExternalLink } from "lucide-react";

const META = {
  "Baner": { zone: "Pune West", desc: "Premium IT-corridor pocket. Strong rental yields, mature schools, low new-supply." },
  "Balewadi": { zone: "Pune West", desc: "Sports City + Mumbai-Pune expressway access. Mid-premium mix." },
  "Wakad": { zone: "Pune West", desc: "Stable absorption, consistent inventory. Excellent connectivity to Hinjewadi." },
  "Tathawade": { zone: "Pune West", desc: "Rapid infra upgrade — emerging investment zone with strong upside." },
  "Kharadi": { zone: "Pune East", desc: "EON IT Park-led appreciation. Pune's hottest East corridor." },
  "Viman Nagar": { zone: "Pune East", desc: "Established premium pocket near airport. Limited new supply, durable prices." },
  "Wagholi": { zone: "Pune East", desc: "Affordable entry-point. Sharp upside as Ring Road completes." },
  "Magarpatta": { zone: "Pune East", desc: "Walk-to-work flagship township. Consistent end-user demand, tight resale supply." },
  "Mundhwa": { zone: "Pune East", desc: "Riverside redevelopment + Keshav Nagar bridge unlocking pricing potential." },
};

export default function MarketInsights() {
  const agent = useAgent();
  const [data, setData] = useState({});
  const [compliant, setCompliant] = useState(null);
  useEffect(() => {
    api.get("/market-insights").then(({ data }) => setData(data));
    api.get("/compliant-market-insights").then(({ data }) => setCompliant(data)).catch(() => setCompliant(null));
  }, []);

  const west = Object.entries(data).filter(([loc]) => META[loc]?.zone === "Pune West");
  const east = Object.entries(data).filter(([loc]) => META[loc]?.zone === "Pune East");

  const inner = (
    <>
      <section className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-sg-gold mb-5">PUNE MARKET PULSE · LIVE</div>
          <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
            Year-on-year movement across our <span className="font-serif-italic text-sg-gold">Pune micro-markets.</span>
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl">
            {agent
              ? `${agent.name.split(" ")[0]} pulls these benchmarks live from MahaCRITI registrations, agent transaction telemetry, and developer base-price changes. Refreshed monthly.`
              : "Drawn from MahaCRITI registrations, our agent transaction telemetry, and developer base-price changes. Numbers refreshed monthly."}
          </p>
        </div>
      </section>

      <Section title="Pune West" tag="WEST CORRIDOR" rows={west} agentLocality={agent?.primary_locality} />
      <Section title="Pune East" tag="EAST CORRIDOR" rows={east} agentLocality={agent?.primary_locality} cream />

      {compliant && !compliant.empty && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="overline text-sg-gold-deep mb-3">OFFICIAL SOURCES · COMPLIANT BRIEFING</div>
          <h2 className="font-display text-4xl text-sg-navy">
            Regulatory pulse from <span className="font-serif-italic">MahaRERA + IGR.</span>
          </h2>
          <p className="text-sg-mute mt-2 max-w-3xl">
            Synthesised from public government dashboards. Numbers are extracted verbatim — no speculation.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <CompliantBucket title="Supply Growth" tone="gold" bullets={compliant.bullets?.supply_growth} testId="compliant-supply" />
            <CompliantBucket title="Infra Impact" tone="navy" bullets={compliant.bullets?.infra_impact} testId="compliant-infra" />
            <CompliantBucket title="Regulatory Update" tone="cream" bullets={compliant.bullets?.regulatory_update} testId="compliant-regulatory" />
          </div>
          <div className="mt-8 rounded-2xl bg-sg-cream-soft border border-sg-line p-5 flex flex-col md:flex-row items-start gap-4 text-sm">
            <ShieldCheck className="w-5 h-5 text-sg-gold-deep mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-sg-navy" data-testid="compliant-disclaimer">
                {compliant.disclaimer}
              </div>
              {compliant.generated_at && (
                <div className="text-xs text-sg-mute mt-1 font-mono">Last refresh · {new Date(compliant.generated_at).toLocaleString()}</div>
              )}
              {compliant.sources && compliant.sources.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  {compliant.sources.map((s) => (
                    <a
                      key={s.url}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sg-navy hover:text-sg-gold-deep underline decoration-dotted underline-offset-2"
                      data-testid={`compliant-source-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {s.name} <ExternalLink className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <div className="sg-card-dark flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="overline text-sg-gold mb-2">{agent ? "TALK TO " + agent.name.toUpperCase() : "FOR AGENTS"}</div>
            <h3 className="font-display text-3xl leading-tight">
              {agent
                ? <>Want to act on these <span className="font-serif-italic text-sg-gold">micro-market trends?</span></>
                : <>Embed live market insights on your <span className="font-serif-italic text-sg-gold">own branded portal.</span></>}
            </h3>
            <p className="text-sm text-white/75 mt-3 max-w-lg">
              {agent
                ? `Tap WhatsApp to send ${agent.name.split(" ")[0]} a brief on which corridor or budget you're tracking — and get a curated shortlist within 24 hours.`
                : "Pro Plan agents get the locality YoY widget auto-injected next to every project — and a webhook every time a buyer taps it."}
            </p>
          </div>
          <Link to={agent ? `/agent/${agent.slug}/buy` : "/login"} className="pill-solid inline-flex items-center gap-2">
            {agent ? "Browse Inventory" : "Claim Your Portal"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );

  return agent ? inner : <PublicLayout>{inner}</PublicLayout>;
}

function Section({ title, tag, rows, cream, agentLocality }) {
  return (
    <section className={`${cream ? "bg-white border-y border-sg-line" : ""} py-16`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="overline text-sg-gold-deep mb-3">{tag}</div>
        <h2 className="font-display text-4xl text-sg-navy">
          {title} <span className="text-sg-mute text-2xl align-middle">· {rows.length} micro-markets</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {rows.map(([loc, insight]) => {
            const isPrimary = loc === agentLocality;
            return (
              <div key={loc} className={`sg-card p-6 ${isPrimary ? "ring-2 ring-sg-gold" : ""}`} data-testid={`insight-${loc.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between gap-3 text-sg-mute">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <span className="overline text-sg-mute">{loc}</span>
                  </div>
                  {isPrimary && <span className="chip chip-gold text-[10px]">MY HOME TURF</span>}
                </div>
                <div className="flex items-end justify-between mt-4">
                  <div className="rupee text-4xl text-sg-navy">{insight.yoy}</div>
                  <TrendingUp className="w-4 h-4 text-sg-gold-deep" />
                </div>
                <p className="text-xs text-sg-mute mt-3 leading-relaxed">{insight.summary}</p>
                {META[loc]?.desc && (
                  <p className="text-xs text-sg-mute mt-3 border-t border-dashed border-sg-line pt-3 italic">{META[loc].desc}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CompliantBucket({ title, bullets, tone, testId }) {
  const items = (bullets || []).slice(0, 3);
  const toneMap = {
    gold: "bg-sg-gold/10 border border-sg-gold/40",
    navy: "bg-sg-navy text-white",
    cream: "bg-sg-cream-soft border border-sg-line",
  };
  const isDark = tone === "navy";
  return (
    <div className={`rounded-3xl p-6 ${toneMap[tone] || toneMap.cream}`} data-testid={testId}>
      <div className={`overline ${isDark ? "text-sg-gold" : "text-sg-gold-deep"} mb-3`}>{title}</div>
      {items.length === 0 ? (
        <div className={`text-sm italic ${isDark ? "text-white/60" : "text-sg-mute"}`}>
          Awaiting next refresh cycle.
        </div>
      ) : (
        <ul className={`space-y-2 text-sm ${isDark ? "text-white/85" : "text-sg-navy"}`}>
          {items.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${isDark ? "bg-sg-gold" : "bg-sg-gold-deep"}`} />
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
